import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSupplier } from '../queryKeys';

import { fetchSupplierHistory } from './fetchSupplierHistory';
import {
  type SupplierHistoryQueryParams,
  type SupplierHistoryResponse,
} from './types';

/**
 * Generates the query options for the supplier history query so they can be
 * shared between the useQuery hook and prefetching in route loaders.
 *
 * @param deId - The data exchange ID of the supplier relationship.
 * @param queryParams - Optional pagination parameters.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and staleTime.
 */
export const getSupplierHistoryQueryOptions = <
  TData = SupplierHistoryResponse,
>({
  deId,
  queryParams = {},
  options,
}: {
  deId: UUID | undefined;
  queryParams?: SupplierHistoryQueryParams;
  options?: Omit<
    UseQueryOptions<SupplierHistoryResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    placeholderData: keepPreviousData,
    queryFn: deId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchSupplierHistory(deId, queryParams, { signal })
      : skipToken,
    queryKey: queryKeysSupplier.getHistory(
      deId ?? '',
      queryParams.limit,
      queryParams.offset,
    ),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });

/**
 * React Query based custom hook for fetching the change history of a supplier.
 *
 * Uses keepPreviousData to prevent flickering when the page changes.
 *
 * @param deId - The data exchange ID of the supplier relationship.
 * @param queryParams - Optional pagination parameters.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook containing the supplier history.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Supplier-details/operation/get_supplier_history_suppliers_details__de_id__history_get
 */
export const useQuerySupplierHistory = <TData = SupplierHistoryResponse>(
  deId: UUID | undefined,
  queryParams: SupplierHistoryQueryParams = {},
  options?: Omit<
    UseQueryOptions<SupplierHistoryResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getSupplierHistoryQueryOptions<TData>({ deId, queryParams, options }),
  );
